import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import StudentDictationMistakes from "./StudentDictationMistakes";
import { studentReportMistakes } from "../utils/studentReportMistakes";

import type { StudentReport } from "@lingoshade/domain";

interface Props {
  details: NonNullable<StudentReport["details"]>;
}
export default function StudentReportDetails({
  details,
}: Props): React.ReactElement {
  const { t, i18n } = useTranslation("texts");
  const date = (value: string): string =>
    new Date(value).toLocaleString(i18n.language, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  return (
    <Stack spacing={2}>
      {details.dictations.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("students.dictations")}
          </Typography>
          {details.dictations.map((entry): React.ReactElement => {
            const mistakes = studentReportMistakes(entry.mistakes);
            return (
              <Accordion
                key={entry.id}
                disableGutters
                elevation={0}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ width: "100%", alignItems: "center" }}
                  >
                    <Box>
                      <Typography variant="body2">
                        {date(entry.completedAt || entry.startedAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("students.report.errorsSummary", {
                          count: entry.mistakes.length,
                          saved: mistakes.savedWords,
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                      <Typography fontWeight={600}>
                        {entry.accuracy === null
                          ? t("students.inProgress")
                          : `${entry.accuracy.toFixed(1)}%`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("students.seconds", {
                          count: Math.round(entry.timeSpentMs / 1000),
                        })}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <StudentDictationMistakes report={mistakes} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
      {details.practice.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("students.practice")}
          </Typography>
          {details.practice.map(
            (entry): React.ReactElement => (
              <Stack
                key={entry.id}
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{ py: 0.75, borderBottom: 1, borderColor: "divider" }}
              >
                <Typography variant="body2">{date(entry.startedAt)}</Typography>
                <Typography variant="body2">
                  {t("students.seconds", { count: entry.activeSeconds })}
                </Typography>
              </Stack>
            ),
          )}
        </Box>
      )}
      {details.words.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("students.report.savedWords")}
          </Typography>
          {details.words.map(
            (word): React.ReactElement => (
              <Stack
                key={word.id}
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{ py: 0.75, borderBottom: 1, borderColor: "divider" }}
              >
                <Box>
                  <Typography variant="body2">
                    {word.word} · {word.translation}
                  </Typography>
                  {word.fromDictation && (
                    <Typography variant="caption" color="text.secondary">
                      {t("students.fromDictation")}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {word.addedAt ? date(word.addedAt) : "—"}
                </Typography>
              </Stack>
            ),
          )}
        </Box>
      )}
      {details.reviews.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("students.report.wordReviews")}
          </Typography>
          {details.reviews.map(
            (review): React.ReactElement => (
              <Stack
                key={review.id}
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{ py: 0.75, borderBottom: 1, borderColor: "divider" }}
              >
                <Box>
                  <Typography variant="body2">{review.word}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {date(review.studiedAt)}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color={review.correct ? "success.main" : "text.secondary"}
                >
                  {t(
                    review.correct ? "students.correct" : "students.incorrect",
                  )}
                </Typography>
              </Stack>
            ),
          )}
        </Box>
      )}
      {!details.practice.length &&
        !details.dictations.length &&
        !details.words.length &&
        !details.reviews.length && (
          <Typography color="text.secondary">
            {t("students.noActivity")}
          </Typography>
        )}
    </Stack>
  );
}
